const GOOGLE_SEARCH_JOBS = new Set([
  'schwab-copilot',
  'tos-scanner',
  'portfolio-monitor',
  'schwab-ai-control',
]);

const GOOGLE_SEARCH_SIGNUP_VARIANTS = new Set(['control', 'auth_first']);

export type GoogleSearchLeadAttribution = {
  googleSearchLpJob: string;
  googleSearchSignupExperienceV2Variant: string;
  landingPage?: string;
  referrer?: string;
  source?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmMedium?: string;
  utmSource?: string;
  utmTerm?: string;
};

type AttributionContext = Record<string, unknown> | null | undefined;

const readContextString = (context: AttributionContext, key: string): string | null => {
  const value = context?.[key];
  return typeof value === 'string' && value.trim() ? value.trim() : null;
};

const parseInternalDestination = (destination?: string | null): URL | null => {
  if (!destination || typeof window === 'undefined') return null;
  try {
    return new URL(destination, window.location.origin);
  } catch {
    return null;
  }
};

const optionalValue = (...values: Array<string | null | undefined>): string | undefined => (
  values.find((value) => typeof value === 'string' && value.trim())?.trim()
);

export const buildGoogleSearchLeadAttribution = ({
  context,
  destination,
}: {
  context?: AttributionContext;
  destination?: string | null;
} = {}): GoogleSearchLeadAttribution | null => {
  const parsedDestination = parseInternalDestination(destination);
  const destinationParams = parsedDestination?.searchParams;
  const job = optionalValue(
    readContextString(context, 'google_search_lp_job'),
    destinationParams?.get('google_search_lp_job'),
    destinationParams?.get('job'),
  );
  const variant = optionalValue(
    readContextString(context, 'google_search_signup_experience_v2_variant'),
    destinationParams?.get('google_search_signup_experience_v2_variant'),
  );

  if (!job || !variant || !GOOGLE_SEARCH_JOBS.has(job) || !GOOGLE_SEARCH_SIGNUP_VARIANTS.has(variant)) {
    return null;
  }

  const currentUrl = typeof window !== 'undefined' ? window.location.href : undefined;
  const currentReferrer = typeof document !== 'undefined' ? document.referrer : undefined;
  return {
    googleSearchLpJob: job,
    googleSearchSignupExperienceV2Variant: variant,
    landingPage: optionalValue(readContextString(context, 'landing_page'), currentUrl),
    referrer: optionalValue(readContextString(context, 'referrer'), currentReferrer),
    source: optionalValue(readContextString(context, 'source'), 'google_search_auth_first'),
    utmCampaign: optionalValue(
      readContextString(context, 'utm_campaign'),
      destinationParams?.get('utm_campaign'),
    ),
    utmContent: optionalValue(
      readContextString(context, 'utm_content'),
      destinationParams?.get('utm_content'),
    ),
    utmMedium: optionalValue(
      readContextString(context, 'utm_medium'),
      destinationParams?.get('utm_medium'),
    ),
    utmSource: optionalValue(
      readContextString(context, 'utm_source'),
      destinationParams?.get('utm_source'),
    ),
    utmTerm: optionalValue(
      readContextString(context, 'utm_term'),
      destinationParams?.get('utm_term'),
    ),
  };
};
